export interface TextStats {
  lines: number
  words: number
  chars: number
  charsNoSpace: number
}

export interface DeduplicationResult {
  output: string
  removedLines: number
  removedWords: number
}

function parseWords(line: string): string[] {
  return line.split(/\s+/).filter((w) => w.trim().length > 0)
}

function normalizeKey(value: string, caseSensitive: boolean): string {
  return caseSensitive ? value : value.toLowerCase()
}

function deduplicateWordsInLine(line: string, caseSensitive: boolean): string {
  if (line === '') return ''
  const words = parseWords(line)
  const seen = new Map<string, string>()
  words.forEach((word) => {
    const key = normalizeKey(word, caseSensitive)
    if (!seen.has(key)) seen.set(key, word)
  })
  return [...seen.values()].join(' ')
}

export function computeStats(text: string): TextStats {
  if (!text) return { lines: 0, words: 0, chars: 0, charsNoSpace: 0 }
  // Match original: count ALL lines (split by '\n'), including empty ones
  const allLines = text.split('\n')
  const lineCount = allLines.length
  // Words: split on whitespace, filter empties (original also excludes bare newline tokens)
  const words = text.split(/\s+/).filter((w) => w.trim().length > 0)
  // Chars: join only non-empty lines with '\n', measure length (matches original)
  const chars = allLines.filter((l) => l.trim().length > 0).join('\n').length
  const charsNoSpace = text.replace(/\s/g, '').length
  return { lines: lineCount, words: words.length, chars, charsNoSpace }
}

export function removeLineDuplicates(
  inputText: string,
  caseSensitive: boolean
): DeduplicationResult {
  const lines = inputText.split('\n').map((l) => l.trim())

  // Count total words in input
  const wordCounts = new Map<string, number>()
  lines.forEach((line) => {
    if (line !== '') {
      parseWords(line).forEach((word) => {
        const key = normalizeKey(word, caseSensitive)
        wordCounts.set(key, (wordCounts.get(key) ?? 0) + 1)
      })
    }
  })

  const lineMap = new Map<string, string>()
  lines.forEach((line) => {
    if (line !== '') {
      const key = normalizeKey(line, caseSensitive)
      if (!lineMap.has(key)) lineMap.set(key, line)
    }
  })

  const uniqueLines = [...lineMap.values()]
  const output = uniqueLines.join('\n')

  const inputNonEmpty = lines.filter((l) => l !== '').length
  const removedLines = inputNonEmpty - uniqueLines.length

  let totalWords = 0
  wordCounts.forEach((count) => (totalWords += count))
  const uniqueWordsCount = uniqueLines.reduce((sum, line) => {
    const words = parseWords(line)
    return (
      sum +
      new Set(caseSensitive ? words : words.map((w) => w.toLowerCase())).size
    )
  }, 0)

  return { output, removedLines, removedWords: totalWords - uniqueWordsCount }
}

export function removeWordDuplicates(
  inputText: string,
  caseSensitive: boolean
): DeduplicationResult {
  const lines = inputText.split('\n').map((l) => l.trim())

  let totalWords = 0
  lines.forEach((line) => {
    if (line !== '') totalWords += parseWords(line).length
  })

  const uniqueLines = lines
    .map((line) => {
      if (line === '') return ''
      return deduplicateWordsInLine(line, caseSensitive)
    })
    .filter((l) => l !== '')

  const output = uniqueLines.join('\n')
  const uniqueWordsCount = uniqueLines.reduce(
    (sum, line) => sum + parseWords(line).length,
    0
  )

  return { output, removedLines: 0, removedWords: totalWords - uniqueWordsCount }
}

export function applyTextOps(
  text: string,
  opts: { removeEmpty: boolean; trim: boolean; sort: boolean }
): string {
  if (!opts.removeEmpty && !opts.trim && !opts.sort) return text
  let lines = text.split('\n')
  if (opts.trim) lines = lines.map((l) => l.trim())
  if (opts.removeEmpty) lines = lines.filter((l) => l.length > 0)
  if (opts.sort) lines = [...lines].sort((a, b) => a.localeCompare(b))
  return lines.join('\n')
}

export function removeLineAndWordDuplicates(
  inputText: string,
  caseSensitive: boolean
): DeduplicationResult {
  const lines = inputText
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l !== '')

  const wordCounts = new Map<string, number>()
  lines.forEach((line) => {
    parseWords(line).forEach((word) => {
      const key = normalizeKey(word, caseSensitive)
      wordCounts.set(key, (wordCounts.get(key) ?? 0) + 1)
    })
  })
  let totalWords = 0
  wordCounts.forEach((count) => (totalWords += count))

  const lineMap = new Map<string, string>()
  lines.forEach((line) => {
    const deduped = deduplicateWordsInLine(line, caseSensitive)
    const key = normalizeKey(deduped, caseSensitive)
    if (!lineMap.has(key)) lineMap.set(key, deduped)
  })

  const uniqueLines = [...lineMap.values()]
  const output = uniqueLines.join('\n')

  const removedLines = lines.length - uniqueLines.length
  const uniqueWordsCount = uniqueLines.reduce((sum, line) => {
    const words = parseWords(line)
    return (
      sum +
      new Set(caseSensitive ? words : words.map((w) => w.toLowerCase())).size
    )
  }, 0)

  return { output, removedLines, removedWords: totalWords - uniqueWordsCount }
}
